/**
 * LeaveBalances — per-worker leave balance snapshot.
 *
 * Migrated to live API in Wave 3.2. Backend returns flat balance rows
 * keyed by (worker_id, leave_type_id, year). The page pivots those
 * client-side into a table with one row per worker and one column
 * per active leave type so HR can scan usage at a glance.
 *
 * Read-only. Balance values come from policy entitlements +
 * accumulated usage; there's no mutate-balance endpoint exposed on
 * the tenant surface (operators handle corrections at the policy /
 * approval level instead).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { RefreshOutlined, TuneOutlined, CloseOutlined } from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type { LeaveBalance, LeaveType } from '../../../api/attendance';
import type { Schema } from '../../../api/types';
import { useTranslation } from 'react-i18next';
import { ExportButton } from '../../../components/primitives';
import { translateApiError } from '../../../i18n/translateApiError';

type Worker = Schema<'WorkerResponse'>;
type Department = Schema<'DepartmentResponse'>;

function balanceColor(used: number, entitlement: number): string {
  if (entitlement === 0) return 'text.secondary';
  const remaining = (entitlement - used) / entitlement;
  if (remaining > 0.5) return 'success.main';
  if (remaining >= 0.2) return 'warning.main';
  return 'error.main';
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

const LeaveBalances: React.FC = () => {
  const { t } = useTranslation();
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterText, setFilterText] = useState('');
  const [locFilter, setLocFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Adjust dialog
  const [adjustTarget, setAdjustTarget] = useState<LeaveBalance | null>(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bs, ts, es, ds, ls] = await Promise.all([
        attendanceApi.leaveBalancesApi.list(),
        attendanceApi.leaveTypesApi.list(),
        organizationApi.workersApi.list(),
        organizationApi.departmentsApi.list(),
        organizationApi.locationsApi.list(),
      ]);
      setBalances(bs);
      setTypes(ts.filter((t) => t.status === 'active'));
      setWorkers(es);
      setDepartments(ds);
      setLocations(ls.map((l) => ({ id: l.id, name: l.name })));
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:leaveBalances.loadFailed'));
      setBalances([]);
      setTypes([]);
      setWorkers([]);
      setDepartments([]);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deptById = useMemo(() => {
    const m = new Map<string, Department>();
    for (const d of departments) m.set(d.id, d);
    return m;
  }, [departments]);

  // Pivot: empId → leaveTypeId → balance
  const pivot = useMemo(() => {
    const m = new Map<string, Map<string, LeaveBalance>>();
    for (const b of balances) {
      if (!m.has(b.worker_id)) m.set(b.worker_id, new Map());
      m.get(b.worker_id)!.set(b.leave_type_id, b);
    }
    return m;
  }, [balances]);

  const filtered = useMemo(() => {
    const q = filterText.toLowerCase();
    return workers.filter((e) => {
      if (locFilter !== 'all' && e.primary_location_id !== locFilter) return false;
      if (deptFilter !== 'all' && e.primary_department_id !== deptFilter) return false;
      if (!q) return true;
      const dept = deptById.get(e.primary_department_id ?? '')?.name ?? '';
      const name = `${e.first_name} ${e.last_name}`;
      return name.toLowerCase().includes(q) || dept.toLowerCase().includes(q);
    });
  }, [workers, locFilter, deptFilter, filterText, deptById]);

  // Leave Type filter narrows visible columns (convention §3 "What").
  const visibleTypes = useMemo(
    () => leaveTypeFilter === 'all' ? types : types.filter((t) => t.id === leaveTypeFilter),
    [types, leaveTypeFilter],
  );

  // Accrued leave is a liability on the books and a number payroll has to settle when
  // someone leaves, so this page needed an export and had none.
  //
  // LONG format — one row per worker per leave type — deliberately, not the pivot the
  // screen shows. Two reasons, and both are about the consumer rather than tidiness:
  // the screen's cell is the string "5 / 20", which Excel imports as TEXT and cannot
  // sum; and a pivot's width grows with the number of leave types, so the file's shape
  // would change every time a customer adds one. Long format is also what a
  // spreadsheet's own pivot wants as input.
  const exportColumns = useMemo(() => [
    t('attendance:leaveBalances.colWorker'),
    t('attendance:leaveBalances.colDepartment'),
    t('attendance:leaveBalances.colLeaveType'),
    t('attendance:leaveBalances.colEntitled'),
    t('attendance:leaveBalances.colUsed'),
    t('attendance:leaveBalances.colRemaining'),
  ], [t]);

  const exportRows = useMemo(() => {
    const out: Record<string, unknown>[] = [];
    for (const worker of filtered) {
      const balancesFor = pivot.get(worker.id);
      const dept = deptById.get(worker.primary_department_id ?? '')?.name ?? '';
      for (const lt of visibleTypes) {
        const b = balancesFor?.get(lt.id);
        // A worker with no balance row for a type is SKIPPED rather than exported as
        // zeroes: "0 remaining" and "this type does not apply to them" are different
        // facts, and writing the first would be a wrong number in a payroll file.
        if (!b) continue;
        out.push({
          [exportColumns[0]]: `${worker.first_name ?? ''} ${worker.last_name ?? ''}`.trim(),
          [exportColumns[1]]: dept,
          [exportColumns[2]]: lt.name,
          [exportColumns[3]]: b.entitlement,
          [exportColumns[4]]: b.used,
          [exportColumns[5]]: b.entitlement - b.used,
        });
      }
    }
    return out;
  }, [filtered, visibleTypes, pivot, deptById, exportColumns]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 1.5, gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:leaveBalances.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:leaveBalances.subtitleDetail')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <ExportButton
            filename={`leave-balances-${new Date().toISOString().slice(0, 10)}`}
            title={t('attendance:leaveBalances.title')}
            columns={exportColumns}
            rows={exportRows}
          />
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      <Box sx={{ width: '100%', display: 'flex', gap: 1.5, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('attendance:timeEntries.locationLabel')}</InputLabel>
          <Select value={locFilter} label={t('attendance:timeEntries.locationLabel')}
            onChange={e => { setLocFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('attendance:timeEntries.allLocations')}</MenuItem>
            {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('attendance:leaveBalances.colDepartment')}</InputLabel>
          <Select value={deptFilter} label={t('attendance:timeEntries.departmentLabel')} onChange={e => { setDeptFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('attendance:timeEntries.allDepartments')}</MenuItem>
            {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('attendance:leavePolicies.leaveTypeLabel')}</InputLabel>
          <Select value={leaveTypeFilter} label={t('attendance:leavePolicies.leaveTypeLabel')}
            onChange={e => { setLeaveTypeFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('attendance:leaveBalances.allTypes')}</MenuItem>
            {types.map(lt => <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label={t('attendance:leaveBalances.filterLabel', { defaultValue: 'Search worker' })} value={filterText}
          onChange={e => { setFilterText(e.target.value); setPage(0); }}
          size="small" sx={{ width: 240 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filtered.length}
          rowsPerPage={rowsPerPage} page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }} />
      </Box>

      {/* Active filter chips — FILTER_DESIGN_CONVENTION §5 */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (locFilter !== 'all') {
          const name = locations.find(l => l.id === locFilter)?.name ?? locFilter;
          chips.push({ key: 'loc', label: `Location: ${name}`,
            onClear: () => { setLocFilter('all'); setPage(0); } });
        }
        if (deptFilter !== 'all') {
          const name = departments.find(d => d.id === deptFilter)?.name ?? deptFilter;
          chips.push({ key: 'dept', label: `Department: ${name}`,
            onClear: () => { setDeptFilter('all'); setPage(0); } });
        }
        if (leaveTypeFilter !== 'all') {
          const name = types.find(t => t.id === leaveTypeFilter)?.name ?? leaveTypeFilter;
          chips.push({ key: 'lt', label: `Leave type: ${name}`,
            onClear: () => { setLeaveTypeFilter('all'); setPage(0); } });
        }
        if (filterText.trim()) {
          chips.push({ key: 'search', label: `Search: ${filterText.trim()}`,
            onClear: () => { setFilterText(''); setPage(0); } });
        }
        if (chips.length === 0) return null;
        return (
          <Box sx={{ width: '100%', display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {chips.map(c => (
              <Chip key={c.key} label={c.label} size="small" onDelete={c.onClear}
                variant="outlined" color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }} />
            ))}
            <Button size="small"
              onClick={() => {
                setLocFilter('all'); setDeptFilter('all');
                setLeaveTypeFilter('all'); setFilterText(''); setPage(0);
              }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 0, px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}>
              Clear all
            </Button>
          </Box>
        );
      })()}

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : types.length === 0 ? (
        <Alert icon={false} severity="info" sx={{ width: '100%' }}>
          No leave types defined yet. Balances can't render without type columns.
        </Alert>
      ) : (
        <>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {t('attendance:leaveBalances.adjustHint')}
        </Typography>
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('attendance:leaveBalances.colWorker')}</TableCell>
                <TableCell>{t('attendance:leaveBalances.colDepartment')}</TableCell>
                {visibleTypes.map(lt => <TableCell key={lt.id} align="center">{lt.name}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map(worker => {
                const name = `${worker.first_name} ${worker.last_name}`.trim();
                const dept = deptById.get(worker.primary_department_id ?? '')?.name ?? '—';
                const empBalances = pivot.get(worker.id) ?? new Map<string, LeaveBalance>();
                return (
                  <TableRow key={worker.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>{initials(name)}</Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="caption">{dept}</Typography></TableCell>
                    {visibleTypes.map(lt => {
                      const b = empBalances.get(lt.id);
                      if (!b) return <TableCell key={lt.id} align="center"><Typography variant="caption" color="text.disabled">—</Typography></TableCell>;
                      const remaining = b.entitlement - b.used;
                      return (
                        <TableCell key={t.id} align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: balanceColor(b.used, b.entitlement) }}>
                                {remaining}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                {' '} / {b.entitlement}
                              </Typography>
                            </Box>
                            <Tooltip title={t('attendance:leaveBalances.adjustTooltip')}>
                              <IconButton size="small" onClick={() => { setAdjustTarget(b); setAdjustAmount(''); setAdjustReason(''); }}
                                sx={{ p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                                <TuneOutlined sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {paginated.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={types.length + 2} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {filterText || deptFilter !== 'all' ? t('attendance:leaveBalances.noMatch') : t('attendance:leaveBalances.noWorkers')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {/* Adjust Dialog */}
      <Dialog open={!!adjustTarget} onClose={() => setAdjustTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('attendance:leaveBalances.adjustTitle')}</Typography>
          <IconButton size="small" onClick={() => setAdjustTarget(null)}><CloseOutlined fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={2}>
            <TextField label={t('attendance:leaveBalances.amountLabel')} type="number" size="small" fullWidth required
              value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)}
              helperText={t('attendance:leaveBalances.amountHelper')} />
            <TextField label={t('attendance:leaveBalances.reasonLabel')} size="small" fullWidth
              value={adjustReason} onChange={e => setAdjustReason(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setAdjustTarget(null)} size="small" sx={{ borderRadius: 1, textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" size="small" disabled={!adjustAmount || adjustSaving}
            sx={{ borderRadius: 1, textTransform: 'none' }}
            onClick={async () => {
              if (!adjustTarget) return;
              setAdjustSaving(true);
              try {
                await attendanceApi.leaveBalancesApi.adjust(adjustTarget.id, { amount: parseFloat(adjustAmount), reason: adjustReason || undefined });
                setToast({ message: t('attendance:leaveBalances.adjusted'), severity: 'success' });
                setAdjustTarget(null);
                fetchAll();
              } catch (e) {
                setToast({ message: e instanceof ApiError ? translateApiError(e, t) : t('attendance:leaveBalances.adjustFailed'), severity: 'error' });
              } finally { setAdjustSaving(false); }
            }}>
            {adjustSaving ? t('common:status.saving') : t('attendance:leaveBalances.adjust')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveBalances;
